<template>
  <div>
    <OperacionesFiltersPanel
      :filters.sync="filters"
      :loading="loading"
      :exporting="exporting"
      @clear="resetFilters"
      @search="handleSearch"
      @export="handleExport"
      @open-import="showImportWizard = true"
      @open-upload-pdf="showUploadPdfModal = true"
    />
    <OperacionesTable
      :items="items"
      :loading="loading"
      :load-error="loadError"
      :current-page="currentPage"
      :per-page="perPage"
      :total-rows="totalRows"
      @update:currentPage="handlePageChange"
      @edit-status="openStatusEditor"
    />

    <OperacionesStatusEditor
      v-model="showStatusEditor"
      :saving="savingOc"
      :row="selectedRow"
      @save="handleStatusSave"
      @hidden="selectedRow = null"
    />

    <OperacionesImportWizard
      v-model="showImportWizard"
      :id-periodo="idPeriodo"
      :usuario="usuarioActual"
      @success="handleImportSuccess"
    />

    <OperacionesPdfUploadModal
      v-model="showUploadPdfModal"
      :usuario="usuarioActual"
      @success="handlePdfUploadSuccess"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import OperacionesFiltersPanel from "./OperacionesFiltersPanel.vue";
import OperacionesStatusEditor from "./OperacionesStatusEditor.vue";
import OperacionesTable from "./OperacionesTable.vue";
import OperacionesImportWizard from "./OperacionesImportWizard.vue";
import OperacionesPdfUploadModal from "./OperacionesPdfUploadModal.vue";
import {
  fetchOperaciones,
  guardarOcMigo,
  getOperacionesErrorMessage,
} from "@/services/operaciones/operaciones.api";
import {
  OPERACIONES_DEFAULT_PER_PAGE,
  MESSAGES,
  getOperacionesExportFields
} from "@/services/operaciones/operaciones.constants";
import { getDefaultOperacionesFilters } from "@/services/operaciones/operaciones.contract";
import { exportOperacionesToExcel } from "@/services/operaciones/operaciones.excel";

export default {
  name: "OperacionesTab",

  components: {
    OperacionesFiltersPanel,
    OperacionesTable,
    OperacionesStatusEditor,
    OperacionesImportWizard,
    OperacionesPdfUploadModal,
  },

  data() {
    return {
      filters: getDefaultOperacionesFilters(),
      allItems: [],
      items: [],
      loading: false,
      loadError: "",
      currentPage: 1,
      perPage: OPERACIONES_DEFAULT_PER_PAGE,
      totalRows: 0,
      showStatusEditor: false,
      selectedRow: null,
      savingOc: false,
      exporting: false,
      showImportWizard: false,
      showUploadPdfModal: false,
    };
  },

  computed: {
    ...mapState("module", ["username"]),

    idPeriodo() {
      return Number(this.filters.idPeriodo ?? 0) || null;
    },

    usuarioActual() {
      return this.username ?? "SISTEMA";
    },
  },
  created() {
    if (this.idPeriodo) this.loadData();
  },
  watch: {
    idPeriodo(val) {
      if (!val) {
        this.allItems = [];
        this.items = [];
        this.totalRows = 0;
      }
    },
  },

  methods: {
    resetFilters() {
      this.filters = getDefaultOperacionesFilters();
      this.currentPage = 1;
      if (this.idPeriodo) {
        this.loadData();
      } else {
        this.allItems = [];
        this.items = [];
        this.totalRows = 0;
      }
    },

    async loadData() {
      if (!this.idPeriodo) return;

      this.loading = true;
      this.loadError = "";

      try {
        const data = await fetchOperaciones(
          this.idPeriodo,
          this.filters.idProv,
        );
        this.allItems = data || [];
        this.totalRows = this.allItems.length;
        this.paginate();
      } catch (error) {
        console.error(error);
        this.allItems = [];
        this.items = [];
        this.totalRows = 0;
        this.loadError = getOperacionesErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },

    paginate() {
      const start = (this.currentPage - 1) * this.perPage;
      this.items = this.allItems.slice(start, start + this.perPage);
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.paginate();
    },

    openStatusEditor(row) {
      this.selectedRow = { ...row };
      this.showStatusEditor = true;
    },
    handleSearch() {
      if (!this.idPeriodo) return;
      this.currentPage = 1;
      this.loadData();
    },
    async handleStatusSave(form) {
      this.savingOc = true;
      try {
        await guardarOcMigo({
          idLiquidacionCab: form.idLiquidacionCab,
          codigoTienda: form.codigoTienda,
          numeroOc: form.numeroOc,
          migo: form.migo,
          estadoOc: form.estadoOc,
          usuario: this.usuarioActual,
        });

        await this.loadData();

        this.showStatusEditor = false;
        this.$bvToast.toast(MESSAGES.SAVE_SUCCESS, {
          variant: "success",
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast(
          getOperacionesErrorMessage(error) || MESSAGES.SAVE_ERROR,
          { variant: "danger", solid: true },
        );
      } finally {
        this.savingOc = false;
      }
    },

    handleExport() {
      if (!this.allItems.length) {
        this.$bvToast.toast(MESSAGES.EMPTY_TABLE, {
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.exporting = true;
      try {
        exportOperacionesToExcel(
          this.allItems,
          getOperacionesExportFields(),
          `Operaciones_${this.idPeriodo}.xlsx`,
        );
      } finally {
        this.exporting = false;
      }
    },

    handleImportSuccess() {
      this.loadData();
      this.$bvToast.toast(MESSAGES.IMPORT_SUCCESS, {
        variant: "success",
        solid: true,
      });
    },

    handlePdfUploadSuccess() {
      this.loadData();
      this.$bvToast.toast("Carga de PDFs procesada correctamente.", {
        variant: "success",
        solid: true,
      });
    },
  },
};
</script>